import {IApplicationStatusWorkflowItem} from "../Interfaces/Application/IApplicationStatusWorkflowItem";
import {useRef} from "react";
import {useSelector, useStore} from "react-redux";
import {AppState} from "../store/rootReducer";
import ErrorDialog from "../Components/Dialogs/ErrorDialog";
import ConfirmationDialog from "../Components/Dialogs/ConfirmationDialog";
import QuoteChangeDiffDialog from "../Components/Dialogs/QuoteChangeDiffDialog";
import {AmsLoanPostingDialog} from "../Components/Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingDialog";
import {useConfiguration} from "../Data/ConfigurationData";
import {getFinanceQuoteComparison, getApplicationPrintStatus, FinanceQuoteFieldDiff} from "../Data/FinanceQuoteComparisonData";
import {useDialog} from "../providers/DialogProvider";
import {IApplication} from "../Interfaces/Application/IApplication";
import {getTotalDownPaymentAmountPaid} from "../utils/utils";
import {getBalanceOnQuote, getDownPaymentFromQuote, getLeaseAmountDueRefusal, maxPaymentStreams, postsFromFinanceQuote, usesFundingModule} from "../utils/quoteUtils";
import {toDollars, hasValue, ContractType, ApplicationQuoteType} from "@amsanalytics/react-shared-library";
import {FeatureFlagIds} from "../utils/featureFlagIds";
import {useLeaseQuoteRefusal} from "./UseLeaseQuoteRefusal";
import {getDownPaymentPostingRefusal} from "../utils/downPaymentPostingCheck";
import {unfinishedCardPostingRefusal} from "../payments/downPaymentCards";
import {fetchDownPaymentCardChargesNow} from "../Data/DownPaymentCardData";

export const useLoanPostingDialog = (
  updateApplicationStatusFromStatusWorkflowComponent: (appToSave: IApplication, applicationStatusWorkflowItem: IApplicationStatusWorkflowItem, userBypassedSoftPullRequirement: boolean) => void,
) => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const applicationDownPaymentResponses = useSelector((state: AppState) => state.applications.applicationDownPaymentResponses);
  const previousAmsLoans = useSelector((state: AppState) => state.applications.previousAmsLoans);
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const lastSolvedSelectedFinanceQuote = useSelector((state: AppState) => state.applications.lastSolvedSelectedFinanceQuote);
  const { data: configuration } = useConfiguration();
  const [openDialog, closeDialog] = useDialog();
  const store = useStore<AppState>();
  const showRefusalFor = useLeaseQuoteRefusal();

  // A lease re-solved earlier in this flow (vehicle edit before posting) can have a new Cash Down and cap cost.
  const appForQuoteChecks = () => currentApp?.quoteType === ApplicationQuoteType.Lease
    ? store.getState().applications.editingApp ?? currentApp
    : currentApp;
  
  // Stores quote change acknowledgement data from the diff dialog so it can be
  // injected into postLoanDetails when the AmsLoanPostingDialog's onOK fires.
  const quoteChangeAckRef = useRef<{
    comment: string;
    differencesJson: string;
  } | null>(null);
  
  // Returns whether to continue with the next check
  const conditionallyOpenPreviousLoanErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!previousAmsLoans) {
        openDialog({
          children: (
            <ErrorDialog
              title="Previous AMS Loans"
              errors={['Please try again in a few moments for the previous AMS customers query to complete']}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        // On to the next check or dialog
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check
  const conditionallyOpenIndirectLoanErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!currentApp) {
        resolve(false);
        return;
      }
      
      // An indirect deal that was moved forward to a finance quote has no funding module; the finance quote checks
      // below validate it instead.
      if (usesFundingModule(currentApp, configuration)) {
        if (!currentApp.applicationFunding) {
          openDialog({
            children: (
              <ErrorDialog
                title="Funding Module"
                errors={['The funding module must be completed']}
                closeDialog={() => {
                  closeDialog();
                  resolve(false);
                }}
              />
            ),
          });
        } else {
          const errors: string[] = [];
          if (!currentApp.applicationFunding.aprCompliant) {
            errors.push("The funding module's APR must be compliant");
          }

          if (!currentApp.applicationFunding.carletonAprCompliant) {
            errors.push("The funding module's Carleton APR must be compliant");
          }

          if (!currentApp.applicationFunding.amountFinancedEqualCompliant) {
            errors.push("The funding module's amount financed must be equal to the quote");
          }

          if (!currentApp.applicationFunding.amountFinancedCompliant) {
            errors.push("The funding module's amount financed must be compliant");
          }

          if (hasValue(currentApp.applicationFunding.carletonExceptionMessage)) {
            errors.push(`Carleton Error: ${currentApp.applicationFunding.carletonExceptionMessage}. Fix and re-quote before posting loan.`);
          }

          if (currentApp.applicationFunding.carletonQuoteErrorMessage) {
            errors.push(`Carleton Error (${currentApp.applicationFunding.carletonQuoteErrorMessage.code}): ${currentApp.applicationFunding.carletonQuoteErrorMessage.description}. Fix and re-quote before posting loan.`);
          }

          if (errors.length > 0) {
            openDialog({
              children: (
                <ErrorDialog
                  title="Funding Module Must Be Updated"
                  errors={errors}
                  closeDialog={() => {
                    closeDialog();
                    resolve(false);
                  }}
                />
              ),
            });
          }
          else {
            // On to the next check or dialog
            resolve(true);
          }
        }
      } else {
        // On to the next check or dialog
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check
  const conditionallyOpenCashDealDownPaymentErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!currentApp || !postsFromFinanceQuote(currentApp, configuration)) {
        resolve(true);
        return;
      }

      const isCashDeal = lastSolvedSelectedFinanceQuote?.contractType === ContractType.SinglePayment;
      const totalDownPaymentAmountPaid = getTotalDownPaymentAmountPaid(applicationDownPaymentResponses) ?? 0;

      if (isCashDeal && totalDownPaymentAmountPaid > 0) {
        openDialog({
          children: (
            <ErrorDialog
              title="Cash Deal with Down Payments"
              errors={[`The last solved quote is a cash deal (single payment) with ${toDollars(totalDownPaymentAmountPaid)} in down payments collected. Cash deals cannot be posted with down payments. Please void all down payments or change the contract type and solve again before posting.`]}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check
  // Ensures the remaining loan balance (balance on quote minus down payments) is greater than 0
  const conditionallyOpenLoanFullyPaidErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!currentApp) {
        resolve(true);
        return;
      }

      const balanceOnQuote = getBalanceOnQuote(appForQuoteChecks(), lastSolvedSelectedFinanceQuote, configuration);

      if (balanceOnQuote <= 0) {
        openDialog({
          children: (
            <ErrorDialog
              title="Invalid Loan Balance"
              errors={[`The balance on the last solved quote is ${toDollars(balanceOnQuote)}. The loan cannot be posted with a zero or negative balance. Please re-solve the quote before posting.`]}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check.
  // A card down payment that AMS doesn't have yet blocks posting whatever REQUIRE_DP says (LOS card down payments A12).
  // The rows are read now rather than from the page, since another user may have just charged a card; the server
  // refuses the post too, so a failed read doesn't block here.
  const conditionallyOpenUnfinishedCardDownPaymentDialog = async (): Promise<boolean> => {
    if (!currentApp) {
      return true;
    }

    let refusal: string | undefined;
    try {
      refusal = unfinishedCardPostingRefusal(await fetchDownPaymentCardChargesNow(currentApp.id));
    } catch (err) {
      console.error('Error reading card down payments:', err);
      return true;
    }
    if (!refusal) {
      return true;
    }

    return new Promise<boolean>(resolve => {
      openDialog({
        children: (
          <ErrorDialog
            title="Card Down Payment"
            errors={[refusal!]}
            closeDialog={() => {
              closeDialog();
              resolve(false);
            }}
          />
        ),
      });
    });
  }

  // Returns whether to continue with the next check
  const conditionallyOpenDownPaymentErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      const app = appForQuoteChecks();
      // Indirect clients don't take down payments through this LOS, so the down payment on an indirect quote is never
      // matched against deposits collected. That includes a deal moved forward to a finance quote to print documents
      // internally: its cash down is still collected outside the LOS. A lease collects Total Down less deferred down
      // and needs a solved quote; the amounts are compared only while the tenant's REQUIRE_DP is on.
      const isLease = currentApp?.quoteType === ApplicationQuoteType.Lease;
      const refusal = getDownPaymentPostingRefusal({
        isIndirect: currentApp?.quoteType === ApplicationQuoteType.Indirect,
        isLease,
        leaseRefusal: isLease ? getLeaseAmountDueRefusal(app?.applicationLeaseQuote) : undefined,
        amountDue: getDownPaymentFromQuote(app, lastSolvedSelectedFinanceQuote, configuration),
        paid: getTotalDownPaymentAmountPaid(applicationDownPaymentResponses) ?? 0,
        requireDownPayment: configuration?.requireDownPayment,
      });

      if (refusal) {
        openDialog({
          children: (
            <ErrorDialog
              title="Total Down Payment"
              errors={[refusal]}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        // On to the next check or dialog
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check
  const conditionallyOpenFinanceQuoteErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!currentApp) {
        resolve(false);
        return;
      }

      // Only for a deal that posts from the finance quote (Finance, or an indirect deal that was moved forward)
      if (!postsFromFinanceQuote(currentApp, configuration)) {
        resolve(true);
        return;
      }

      if (!lastSolvedSelectedFinanceQuote) {
        openDialog({
          children: (
            <ErrorDialog
              title="Finance Quote Required"
              errors={['A solved finance quote must be selected to post the loan']}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
        return;
      }

      const errors: string[] = [];

      // Check for Carleton exception message
      if (hasValue(lastSolvedSelectedFinanceQuote.carletonExceptionMessage)) {
        errors.push(`Carleton Error: ${lastSolvedSelectedFinanceQuote.carletonExceptionMessage}. Fix and re-quote before posting loan.`);
      }

      // Check for Carleton quote error message
      if (lastSolvedSelectedFinanceQuote.carletonQuoteErrorMessage) {
        errors.push(`Carleton Error (${lastSolvedSelectedFinanceQuote.carletonQuoteErrorMessage.code}): ${lastSolvedSelectedFinanceQuote.carletonQuoteErrorMessage.description}. Fix and re-quote before posting loan.`);
      }

      // Check for too many payment streams (AMS 2000 can only handle 8)
      const paymentStreamCount = lastSolvedSelectedFinanceQuote.financeQuotePaymentStreams?.length ?? 0;
      if (paymentStreamCount > maxPaymentStreams) {
        errors.push(`There are ${paymentStreamCount} payment streams, but AMS 2000 can only handle ${maxPaymentStreams}. Please reduce the number of irregular payment streams.`);
      }

      if (errors.length > 0) {
        openDialog({
          children: (
            <ErrorDialog
              title="Finance Quote Has Errors"
              errors={errors}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        // On to the next check or dialog
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check
  const conditionallyOpenLeaseQuoteErrorDialog = async (): Promise<boolean> => !showRefusalFor('posting');

  // Returns whether to continue with the next check
  const conditionallyOpenTradeInMismatchErrorDialog = async (): Promise<boolean> => {
    return new Promise(resolve => {
      if (!currentApp || !postsFromFinanceQuote(currentApp, configuration)) {
        resolve(true);
        return;
      }

      const tradeInVehicle = currentApp.tradeInVehicle;
      const tradeInVehicle2 = currentApp.tradeInVehicle2;
      const hasSlot1 = tradeInVehicle && !tradeInVehicle.deletedDate;
      const hasSlot2 = tradeInVehicle2 && !tradeInVehicle2.deletedDate;
      if (!hasSlot1 && !hasSlot2) {
        resolve(true);
        return;
      }

      if (!lastSolvedSelectedFinanceQuote) {
        resolve(true);
        return;
      }

      const tradeIn1Value = !hasSlot1 ? 0 : (tradeInVehicle.tradeValue ?? 0);
      const tradeIn1Balance = !hasSlot1 ? 0 : (tradeInVehicle.tradeBalance ?? 0);
      const tradeIn2Value = currentApp.tradeInVehicle2?.deletedDate ? 0 : (currentApp.tradeInVehicle2?.tradeValue ?? 0);
      const tradeIn2Balance = currentApp.tradeInVehicle2?.deletedDate ? 0 : (currentApp.tradeInVehicle2?.tradeBalance ?? 0);
      const combinedTradeValue = tradeIn1Value + tradeIn2Value;
      const combinedTradeBalance = tradeIn1Balance + tradeIn2Balance;
      const tradeAllowanceMismatch = lastSolvedSelectedFinanceQuote.tradeInValue !== combinedTradeValue;
      const tradeLienMismatch = lastSolvedSelectedFinanceQuote.tradeInPayoff !== combinedTradeBalance;

      if (tradeAllowanceMismatch || tradeLienMismatch) {
        const errors: string[] = [];
        if (tradeAllowanceMismatch) {
          errors.push(`The trade-in allowance on the last solved quote (${toDollars(lastSolvedSelectedFinanceQuote.tradeInValue)}) does not match the combined trade-in trade value (${toDollars(combinedTradeValue)}).`);
        }
        if (tradeLienMismatch) {
          errors.push(`The trade-in lien on the last solved quote (${toDollars(lastSolvedSelectedFinanceQuote.tradeInPayoff)}) does not match the combined trade-in trade balance (${toDollars(combinedTradeBalance)}).`);
        }
        errors.push('Please re-solve the quote before posting.');

        openDialog({
          children: (
            <ErrorDialog
              title="Trade-In Values Out of Sync"
              errors={errors}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
            />
          ),
        });
      } else {
        resolve(true);
      }
    });
  }

  // Returns whether to continue with the next check.
  // If the client can print documents but hasn't printed any yet, show a confirmation dialog.
  const conditionallyOpenDocumentsNotPrintedDialog = async (): Promise<boolean> => {
    if (!currentApp) {
      return true;
    }

    try {
      const printStatus = await getApplicationPrintStatus(currentApp.id);

      // If we couldn't get the status, or they can't print, or they already printed — skip
      if (!printStatus || !printStatus.canPrint || printStatus.hasPrinted) {
        return true;
      }

      // They CAN print but HAVEN'T printed — show confirmation
      return new Promise<boolean>(resolve => {
        openDialog({
          children: (
            <ConfirmationDialog
              title="Documents Not Yet Printed"
              prompt="Documents have not been printed for this application. Are you sure you want to continue posting without printing?"
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
              accept={() => {
                closeDialog();
                resolve(true);
              }}
            />
          ),
        });
      });
    } catch (err) {
      // If the check fails, don't block the posting flow
      console.error('Error checking print status:', err);
      return true;
    }
  }

  // Returns whether to continue with the next check.
  // Compares the current finance quote against the snapshot from when documents were printed.
  // If differences exist, shows a side-by-side diff dialog requiring a mandatory comment.
  const conditionallyOpenQuoteChangeDiffDialog = async (): Promise<boolean> => {
    // Only applies to a deal that posts from the finance quote (Finance, or an indirect deal that was moved forward)
    if (!currentApp || !postsFromFinanceQuote(currentApp, configuration)) {
      return true;
    }

    // Need a selected finance quote to compare
    const selectedQuote = currentApp.financeQuotes?.find(fq => fq.isSelected);
    if (!selectedQuote) {
      return true;
    }

    try {
      const comparisonResult = await getFinanceQuoteComparison(currentApp.id, selectedQuote.id);

      // If we couldn't get a result, no print history, or no differences — skip
      if (!comparisonResult || !comparisonResult.hasPrintHistory || !comparisonResult.hasDifferences) {
        return true;
      }

      // Show the diff dialog and wait for user response
      return new Promise<boolean>(resolve => {
        openDialog({
          children: (
            <QuoteChangeDiffDialog
              differences={comparisonResult.differences}
              deferredPaymentDiffs={comparisonResult.deferredPaymentDiffs}
              irregularPaymentStreamDiffs={comparisonResult.irregularPaymentStreamDiffs}
              printedAt={comparisonResult.printedAt!}
              closeDialog={() => {
                closeDialog();
                resolve(false);
              }}
              accept={(comment: string) => {
                // Store the acknowledgement data so it can be injected into postLoanDetails later
                quoteChangeAckRef.current = {
                  comment,
                  differencesJson: JSON.stringify({
                    fieldDifferences: comparisonResult.differences,
                    deferredPaymentDiffs: comparisonResult.deferredPaymentDiffs,
                    irregularPaymentStreamDiffs: comparisonResult.irregularPaymentStreamDiffs,
                  }),
                };
                closeDialog();
                resolve(true);
              }}
            />
          ),
        });
      });
    } catch (err) {
      // If the comparison API call fails, don't block the posting flow
      console.error('Error checking quote changes:', err);
      return true;
    }
  }

  // Returns whether to continue with the next check.
  // If the loan was already posted to AMS, shows a confirmation dialog warning
  // that continuing will only update the status (not re-post the loan).
  const conditionallyOpenAlreadyPostedConfirmationDialog = async (): Promise<boolean> => {
    if (!currentApp || !hasValue(currentApp.sentToAmsDate)) {
      return true;
    }

    return new Promise<boolean>(resolve => {
      openDialog({
        children: (
          <ConfirmationDialog
            title="Loan Already Posted"
            prompt="This application has already been posted as a loan to AMS 2000. Continuing will only update the status. If you need to re-post the loan, please unwind the loan inside AMS 2000 first."
            closeDialog={() => {
              closeDialog();
              resolve(false);
            }}
            accept={() => {
              closeDialog();
              resolve(true);
            }}
          />
        ),
      });
    });
  }

  const conditionallyOpenVehicleOfSaleRequiredDialog = async (): Promise<boolean> => {
    const allowWithoutVehicle = configuration?.featureFlags?.[FeatureFlagIds.AllowTradeInWithoutVehicleOfSale] === true;
    const hasTradeIn = !!(currentApp?.tradeInVehicle && !hasValue(currentApp.tradeInVehicle.deletedDate))
      || !!(currentApp?.tradeInVehicle2 && !hasValue(currentApp.tradeInVehicle2.deletedDate));
    if (allowWithoutVehicle || currentApp?.vehicle || !hasTradeIn) {
      return true;
    }
    return new Promise(resolve => {
      openDialog({
        children: (
          <ErrorDialog
            title="Vehicle of Sale Required"
            errors={['A vehicle of sale must be attached to the application before posting a loan that has a trade-in vehicle.']}
            closeDialog={() => {
              closeDialog();
              resolve(false);
            }}
          />
        ),
      });
    });
  }

  const openLoanPostingDialog = async (applicationStatusWorkflowItem: IApplicationStatusWorkflowItem, userBypassedSoftPullRequirement: boolean) => {
    // Reset any previously captured acknowledgement data
    quoteChangeAckRef.current = null;
    
    // Run all validation checks in sequence - stops at first failure
    const validationChecks = [
      conditionallyOpenVehicleOfSaleRequiredDialog,
      conditionallyOpenAlreadyPostedConfirmationDialog,
      conditionallyOpenPreviousLoanErrorDialog,
      conditionallyOpenIndirectLoanErrorDialog,
      conditionallyOpenFinanceQuoteErrorDialog,
      conditionallyOpenLeaseQuoteErrorDialog,
      conditionallyOpenUnfinishedCardDownPaymentDialog,
      conditionallyOpenCashDealDownPaymentErrorDialog,
      conditionallyOpenLoanFullyPaidErrorDialog,
      conditionallyOpenDownPaymentErrorDialog,
      conditionallyOpenTradeInMismatchErrorDialog,
      conditionallyOpenDocumentsNotPrintedDialog,
      conditionallyOpenQuoteChangeDiffDialog,
    ];

    for (const check of validationChecks) {
      const shouldContinue = await check();
      if (!shouldContinue) {
        return;
      }
    }

    openDialog({
      children: (
        <AmsLoanPostingDialog
          title={'Create AMS Customer'}
          onOK={(appToSave: IApplication) => {
            // Inject quote change acknowledgement data into postLoanDetails if the user acknowledged changes
            if (quoteChangeAckRef.current && appToSave.postLoanDetails) {
              appToSave.postLoanDetails = {
                ...appToSave.postLoanDetails,
                quoteChangeAcknowledgementComment: quoteChangeAckRef.current.comment,
                quoteChangeDifferencesJson: quoteChangeAckRef.current.differencesJson,
              };
            }
            closeDialog();
            updateApplicationStatusFromStatusWorkflowComponent(appToSave, applicationStatusWorkflowItem, userBypassedSoftPullRequirement);
          }}
          configuration={configuration!}
          lookups={lookups}
          applicationOrganizationId={currentApp!.organizationId}
          closeDialog={closeDialog}
        />
      ),
    });
  }

  // The card check is also run before desking, which moves the deposits to AMS 2000's application number.
  return [openLoanPostingDialog, conditionallyOpenUnfinishedCardDownPaymentDialog] as const;
}